import * as React from 'react';

import type * as api from '@gitbook/api-client';

import { useCreateInOwner } from '../ContentListing';
import type { GetSpace, OrganizationImportTarget } from './types';

/*
 * Returns a function that creates a new space in an organization, ready to be imported into.
 * Once the space is created, future calls to this function will return the existing space.
 * Returns undefined if the user does not have permission to create a space.
 * Throws an error if we try to create a space but the user does not have permission.
 */
export function useOrganizationImportTargetSpace(
    target: OrganizationImportTarget
): GetSpace | undefined {
    const spaceRef = React.useRef<api.Space>();
    const { createSpace } = useCreateInOwner({
        organization: target.organization,
    });

    return React.useMemo(() => {
        return async () => {
            if (spaceRef.current) {
                return spaceRef.current;
            }

            if (!createSpace) {
                throw new Error('Cannot import into shared space due to incorrect hook usage');
            }

            spaceRef.current = await createSpace({ withNavigate: false, source: 'import' });

            return spaceRef.current;
        };
    }, [createSpace]);
}
